import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { useEffect, useState } from "react";

export interface RunPanelProps {
  onSubmit: (params: {
    analyses: string[];
    label?: string;
    model_id?: string;
    revision_no?: number;
  }) => void;
  isSubmitting: boolean;
  columns?: string[];
  models?: Array<{
    id: string;
    name: string;
    active_revision_no?: number;
    label?: string;
    task_type?: string;
  }>;
  onModelSelect?: (modelId: string) => void;
}

interface AnalysisKind {
  key: string;
  label: string;
  requiresLabel: boolean;
  requiresModel: boolean;
}

const PRE_TRAINING_NO_LABEL: AnalysisKind[] = [
  { key: "overview", label: "Overview", requiresLabel: false, requiresModel: false },
  { key: "missing", label: "Missing Values", requiresLabel: false, requiresModel: false },
  { key: "correlations", label: "Correlations", requiresLabel: false, requiresModel: false },
];

const PRE_TRAINING_WITH_LABEL: AnalysisKind[] = [
  { key: "target", label: "Target Distribution", requiresLabel: true, requiresModel: false },
  { key: "quick_fit", label: "Quick Fit", requiresLabel: true, requiresModel: false },
  {
    key: "feature_interaction",
    label: "Feature Interaction",
    requiresLabel: true,
    requiresModel: false,
  },
  {
    key: "correlation_significance",
    label: "Correlation Significance",
    requiresLabel: true,
    requiresModel: false,
  },
];

const DATA_QUALITY: AnalysisKind[] = [
  { key: "target_leakage", label: "Target Leakage", requiresLabel: true, requiresModel: false },
  { key: "class_imbalance", label: "Class Imbalance", requiresLabel: true, requiresModel: false },
  { key: "label_noise", label: "Label Noise", requiresLabel: true, requiresModel: false },
];

const POST_TRAINING: AnalysisKind[] = [
  { key: "confusion_matrix", label: "Confusion Matrix", requiresLabel: true, requiresModel: true },
  {
    key: "roc_pr_calibration_curves",
    label: "ROC, PR & Calibration",
    requiresLabel: true,
    requiresModel: true,
  },
  {
    key: "residual_analysis",
    label: "Residual Analysis (regression only)",
    requiresLabel: true,
    requiresModel: true,
  },
  {
    key: "lift_gain_chart",
    label: "Lift & Gain Chart",
    requiresLabel: true,
    requiresModel: true,
  },
];

/**
 * Analysis kind multi-select + parameter inputs + submit button for EDA workflow.
 * Organizes analysis kinds by category (pre-training, data-quality, post-training).
 */
export function RunPanel({
  onSubmit,
  isSubmitting,
  columns = [],
  models = [],
  onModelSelect,
}: RunPanelProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set(["overview", "missing"]));
  const [label, setLabel] = useState("");
  const [modelId, setModelId] = useState("");
  const [revisionNo, setRevisionNo] = useState("");

  const toggleSelection = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const allKinds = [
    ...PRE_TRAINING_NO_LABEL,
    ...PRE_TRAINING_WITH_LABEL,
    ...DATA_QUALITY,
    ...POST_TRAINING,
  ];
  const needsLabel = allKinds.some((k) => selected.has(k.key) && k.requiresLabel);
  const needsModel = allKinds.some((k) => selected.has(k.key) && k.requiresModel);

  // Sync label from model when model detail arrives async
  useEffect(() => {
    if (modelId && needsModel) {
      const model = models.find((m) => m.id === modelId);
      if (model?.label && label !== model.label) {
        setLabel(model.label);
      }
    }
  }, [models, modelId, needsModel, label]);

  const canSubmit =
    selected.size > 0 &&
    (!needsLabel || label.trim() !== "") &&
    (!needsModel || (modelId.trim() !== "" && revisionNo.trim() !== "")) &&
    !isSubmitting;

  const handleSubmit = () => {
    const params: {
      analyses: string[];
      label?: string;
      model_id?: string;
      revision_no?: number;
    } = {
      analyses: Array.from(selected),
    };
    if (label.trim()) {
      params.label = label.trim();
    }
    if (needsModel && modelId.trim() && revisionNo.trim()) {
      params.model_id = modelId.trim();
      params.revision_no = Number.parseInt(revisionNo, 10);
    }
    onSubmit(params);
  };

  return (
    <div
      className={cn(
        "rounded-[14px] border border-line bg-surface p-5",
        "shadow-[0_1px_2px_rgba(11,20,38,0.04),0_12px_30px_rgba(11,20,38,0.06)]",
      )}
    >
      <h3 className="mb-4 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-navy-300">
        Run Exploratory Data Analysis
      </h3>

      {/* Pre-training (no label) */}
      <div className="mb-4">
        <p className="mb-2 text-xs font-medium text-muted">Pre-training (no label required)</p>
        <div className="flex flex-wrap gap-2">
          {PRE_TRAINING_NO_LABEL.map((kind) => (
            <button
              key={kind.key}
              type="button"
              onClick={() => toggleSelection(kind.key)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                selected.has(kind.key)
                  ? "border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300"
                  : "border-line text-muted hover:border-teal-300",
              )}
            >
              {kind.label}
            </button>
          ))}
        </div>
      </div>

      {/* Pre-training (with label) */}
      <div className="mb-4">
        <p className="mb-2 text-xs font-medium text-muted">Pre-training (label required)</p>
        <div className="flex flex-wrap gap-2">
          {PRE_TRAINING_WITH_LABEL.map((kind) => (
            <button
              key={kind.key}
              type="button"
              onClick={() => toggleSelection(kind.key)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                selected.has(kind.key)
                  ? "border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300"
                  : "border-line text-muted hover:border-teal-300",
              )}
            >
              {kind.label}
            </button>
          ))}
        </div>
      </div>

      {/* Data quality */}
      <div className="mb-4">
        <p className="mb-2 text-xs font-medium text-muted">Data Quality (label required)</p>
        <div className="flex flex-wrap gap-2">
          {DATA_QUALITY.map((kind) => (
            <button
              key={kind.key}
              type="button"
              onClick={() => toggleSelection(kind.key)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                selected.has(kind.key)
                  ? "border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300"
                  : "border-line text-muted hover:border-teal-300",
              )}
            >
              {kind.label}
            </button>
          ))}
        </div>
      </div>

      {/* Post-training */}
      <div className="mb-5">
        <p className="mb-2 text-xs font-medium text-muted">Post-training (model required)</p>
        <div className="flex flex-wrap gap-2">
          {POST_TRAINING.map((kind) => (
            <button
              key={kind.key}
              type="button"
              onClick={() => toggleSelection(kind.key)}
              className={cn(
                "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors",
                selected.has(kind.key)
                  ? "border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/20 dark:text-teal-300"
                  : "border-line text-muted hover:border-teal-300",
              )}
            >
              {kind.label}
            </button>
          ))}
        </div>
      </div>

      {/* Model inputs (conditional) — shown BEFORE label so model auto-fills label */}
      {needsModel && (
        <div className="mb-4 space-y-3">
          <div>
            <label htmlFor="model_id" className="mb-1.5 block text-xs font-medium text-ink">
              Model <span className="text-red-500">*</span>
            </label>
            {models.length > 0 ? (
              <select
                id="model_id"
                value={modelId}
                onChange={(e) => {
                  const id = e.target.value;
                  setModelId(id);
                  const model = models.find((m) => m.id === id);
                  if (model?.active_revision_no) {
                    setRevisionNo(model.active_revision_no.toString());
                  }
                  if (model?.label) {
                    setLabel(model.label);
                  }
                  if (id) onModelSelect?.(id);
                }}
                className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 font-mono text-xs text-ink"
              >
                <option value="">Select model...</option>
                {models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} {m.active_revision_no ? `(rev ${m.active_revision_no})` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id="model_id"
                type="text"
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                placeholder="Enter model ID"
                className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 font-mono text-xs text-ink"
              />
            )}
          </div>
          <div>
            <label htmlFor="revision_no" className="mb-1.5 block text-xs font-medium text-ink">
              Revision Number <span className="text-red-500">*</span>
            </label>
            <input
              id="revision_no"
              type="number"
              value={revisionNo}
              onChange={(e) => setRevisionNo(e.target.value)}
              placeholder="Enter revision number"
              className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 font-mono text-xs text-ink"
            />
          </div>
        </div>
      )}

      {/* Label input (conditional) — after model so model can auto-fill */}
      {needsLabel && (
        <div className="mb-4">
          <label htmlFor="label" className="mb-1.5 block text-xs font-medium text-ink">
            Label Column <span className="text-red-500">*</span>
            {needsModel && modelId && (
              <span className="ml-2 text-[10px] text-muted">(from model training)</span>
            )}
          </label>
          {needsModel && modelId ? (
            <input
              id="label"
              type="text"
              value={label}
              readOnly
              className="w-full rounded-md border border-line bg-code-bg px-2.5 py-1.5 font-mono text-xs text-muted"
            />
          ) : columns.length > 0 ? (
            <select
              id="label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 font-mono text-xs text-ink"
            >
              <option value="">Select label column...</option>
              {columns.map((col) => (
                <option key={col} value={col}>
                  {col}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="label"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Enter label column name"
              className="w-full rounded-md border border-line bg-surface px-2.5 py-1.5 font-mono text-xs text-ink"
            />
          )}
        </div>
      )}

      {/* Submit button */}
      <Button
        onClick={handleSubmit}
        disabled={!canSubmit}
        variant="primary"
        size="md"
        className="w-full"
      >
        {isSubmitting ? "Running..." : "Run Analysis"}
      </Button>
    </div>
  );
}
